import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { resultsApi } from '../lib/resultsApi';
import type { ClassResults, StudentResultRow, AssessmentOption } from '../lib/resultsApi';
import { ChartFrame, HBarChart, StatTile } from '../components/charts';

/**
 * THE CLASS MARK SHEET — "show me 8-A's results."
 *
 * Every child in the class, every subject, totals, percentage and position, plus the toppers and the
 * subject-by-subject picture. This is the paper sheet a head of school has read for thirty years,
 * and the thing they ask for by name.
 *
 * ⚠ POSITIONS ARE SHOWN HERE AND DELIBERATELY NOT ON STUDENTS-AT-RISK. That is not an inconsistency:
 * a staff-facing mark sheet with class positions is ordinary school practice over marks that already
 * exist, whereas a predicted future rank pushed at a child is a different thing entirely. No student
 * ever sees this screen.
 *
 * ⚠ TIES SHARE A POSITION — two children on 87.4% are both 3rd and the next is 5th. Inventing an
 * order between identical scores is a fabrication a parent could fairly challenge.
 */
export function ClassResultsPage() {
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState<ClassResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const classKey = params.get('class');
  const assessment = params.get('exam');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    resultsApi
      .sheet(classKey, assessment)
      .then((r) => {
        if (!alive) return;
        setD(r);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [classKey, assessment]);

  function choose(next: { class?: string; exam?: string | null }) {
    const p = new URLSearchParams(params);
    if (next.class !== undefined) {
      p.set('class', next.class);
      p.delete('exam'); // a different class has different assessments
    }
    if (next.exam !== undefined) {
      if (next.exam) p.set('exam', next.exam);
      else p.delete('exam');
    }
    setParams(p, { replace: true });
  }

  if (loading && !d) return <p style={{ color: 'var(--ink-3)' }}>Reading the mark books…</p>;
  if (failed || !d) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load results.</div>;
  }

  const ranked = d.students.filter((s) => s.position > 0);
  const toppers = ranked.slice(0, 5);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Academics</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Class results</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '72ch' }}>
          Every child in the class, subject by subject, with totals and position. Only subjects a
          child actually sat count towards their total.
        </p>
      </div>

      {/* class picker */}
      <div className="tab-strip" style={{ gap: 8, marginBottom: 10, borderBottom: 'none', alignItems: 'center' }}>
        {d.classes.map((c) => (
          <button
            key={c.classKey}
            type="button"
            className={'chip' + (c.classKey === d.classKey ? ' is-on' : '')}
            onClick={() => choose({ class: c.classKey })}
          >
            {c.classLabel} <span style={{ color: 'var(--ink-3)' }}>· {c.students}</span>
          </button>
        ))}
      </div>

      {/* assessment picker.
          ⚠ AGGREGATES FIRST, then the individual sittings. "Periodic tests · all 3" is the question
          a principal asks ("how are they doing in the periodic tests?"); "Unit test 2" is the
          follow-up. Ordering the picker the other way round buries the useful one behind eight
          chips of noise. */}
      {d.assessments.length > 0 && (
        <>
          <div className="tab-strip" style={{ gap: 8, marginBottom: 8, borderBottom: 'none', alignItems: 'center' }}>
            {d.assessments.filter((a) => a.isAggregate).map((a) => (
              <Chip key={a.key} a={a} on={a.key === d.assessment} pick={() => choose({ exam: a.key })} />
            ))}
          </div>
          <div className="tab-strip" style={{ gap: 8, marginBottom: 14, borderBottom: 'none', alignItems: 'center' }}>
            {d.assessments.filter((a) => !a.isAggregate).map((a) => (
              <Chip key={a.key} a={a} on={a.key === d.assessment} pick={() => choose({ exam: a.key })} />
            ))}
          </div>
        </>
      )}

      {/* ⚠ The rule is stated wherever an aggregate is showing. A column of numbers produced by
          "best 2 of 3" and one produced by a straight average look identical, and a parent who is
          told the wrong one has been given a wrong answer about their child. */}
      {d.assessmentRule && (
        <div className="card" style={{
          padding: '10px 14px', marginBottom: 14, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ink-2)',
        }}>
          <strong>{d.assessmentLabel}</strong> — {d.assessmentRule}. Each column is a percentage,
          because the sittings behind it are out of different totals.
        </div>
      )}

      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        {d.students.length === 0 ? (
          <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>
            {d.note ?? 'No marks for this class yet.'}
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div className="card" style={{ padding: 16 }}>
                <StatTile label="Class average" value={d.classAverage === null ? '—' : `${d.classAverage.toFixed(1)}%`}
                  foot={`${d.sat} students sat`} />
              </div>
              <div className="card" style={{ padding: 16 }}>
                <StatTile label="Highest" value={d.highest === null ? '—' : `${d.highest.toFixed(1)}%`}
                  foot={toppers[0]?.fullName ?? ''} />
              </div>
              <div className="card" style={{ padding: 16 }}>
                <StatTile label="Lowest" value={d.lowest === null ? '—' : `${d.lowest.toFixed(1)}%`}
                  foot={<span style={{ color: 'var(--warn)' }}>worth a conversation</span>} />
              </div>
              <div className="card" style={{ padding: 16 }}>
                {/* ⚠ assessmentLabel, not assessment — the latter is the raw key, and for an
                    aggregate that renders as "agg:3" on the dashboard. */}
                <StatTile label="Subjects" value={String(d.subjectOrder.length)}
                  foot={d.assessmentLabel ?? d.assessment ?? ''} />
              </div>
            </div>

            {/* the toppers — what a principal asks for first */}
            {toppers.length > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <div className="viz-title" style={{ marginBottom: 4 }}>Top of {d.classLabel}</div>
                <p className="viz-sub">
                  Ranked on this assessment. Equal scores share a position, and a student who missed a
                  paper is ranked on the papers they actually sat.
                </p>
                <div style={{ display: 'grid', gap: 8 }}>
                  {toppers.map((s) => (
                    <div
                      key={s.studentId}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px',
                        borderRadius: 8, background: s.position === 1 ? 'var(--warn-bg)' : 'var(--surface-2)',
                      }}
                    >
                      <span style={{
                        width: 26, height: 26, borderRadius: '50%', flex: '0 0 auto',
                        display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
                        background: s.position === 1 ? 'var(--accent)' : 'var(--line-2)',
                        color: s.position === 1 ? '#fff' : 'var(--ink-2)',
                      }}>{s.position}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: 14 }}>{s.fullName}</strong>
                        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 8 }}>
                          {s.admissionNumber}
                        </span>
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                        {s.percent.toFixed(1)}%
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>
                        {s.obtained}/{s.max}
                      </span>
                      {/* ⚠ A child who missed a paper is ranked on a percentage of FEWER papers than
                          everyone above and below them. The mark sheet reveals that through the
                          total (321/400 vs 483/500), but this card does not — so say it. Silently
                          ranking unequal samples is the kind of thing a parent rightly challenges. */}
                      {s.absent > 0 && (
                        <span
                          className="pill pill-warn"
                          style={{ fontSize: 10 }}
                          title="Ranked on the papers this student actually sat"
                        >
                          missed {s.absent}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* subject-by-subject */}
            {d.subjects.length > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <ChartFrame
                  title="How the class did in each subject"
                  subtitle="Class average per subject. The weakest bar is usually a teaching signal rather than a pupil one."
                >
                  <HBarChart
                    categories={d.subjects.map((s) => s.subject)}
                    series={[{ name: 'Class average', color: 'var(--s1)', values: d.subjects.map((s) => s.average) }]}
                    format={(n) => `${n.toFixed(1)}%`}
                    labelWidth={132}
                    valueWidth={68}
                    colorAt={(i) => {
                      const v = d.subjects[i].average;
                      return v >= 60 ? 'var(--ok)' : v >= 45 ? 'var(--warn)' : 'var(--danger)';
                    }}
                  />
                </ChartFrame>
              </div>
            )}

            {/* the full sheet */}
            <div className="card" style={{ padding: 16 }}>
              <div className="viz-title" style={{ marginBottom: 4 }}>
                Full mark sheet — {d.classLabel}
              </div>
              <p className="viz-sub">
                &ldquo;ab&rdquo; is absent; a blank cell means the mark has not been entered yet and does not count
                towards the total.
              </p>

              {/* ⚠ overflowX — a class with ten subjects is wider than a phone, and the alternative is
                  squeezing the page into an unreadable column. */}
              <div style={{ overflowX: 'auto' }}>
                <table className="viz-table" style={{ minWidth: 520 }}>
                  <thead>
                    <tr>
                      <th className="num" style={{ width: 44 }}>#</th>
                      <th>Student</th>
                      {d.subjectOrder.map((s) => (
                        <th key={s} className="num">{s}</th>
                      ))}
                      <th className="num">Total</th>
                      <th className="num">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.students.map((s) => (
                      <Row key={s.studentId} s={s} />
                    ))}
                  </tbody>
                </table>
              </div>

              {d.note && (
                <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10, marginBottom: 0 }}>
                  ⚠ {d.note}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Chip({ a, on, pick }: { a: AssessmentOption; on: boolean; pick: () => void }) {
  return (
    <button type="button" className={'chip' + (on ? ' is-on' : '')} onClick={pick} title={a.rule ?? undefined}>
      {a.label}
      {a.isAggregate && (
        <span style={{ color: 'var(--ink-3)' }}> · {a.rule}</span>
      )}
      {/* ⚠ A scheduled sitting is shown, not hidden — the picker is the exam calendar, and an
          empty mark sheet with an explanation beats a chip that is simply missing. */}
      {a.isScheduled && (
        <span className="pill pill-muted" style={{ fontSize: 9.5, marginLeft: 6 }}>scheduled</span>
      )}
    </button>
  );
}

function Row({ s }: { s: StudentResultRow }) {
  return (
    <tr>
      <td className="num" style={{ color: s.position === 0 ? 'var(--ink-3)' : undefined, fontWeight: 650 }}>
        {s.position === 0 ? '—' : s.position}
      </td>
      <td>
        {s.fullName}
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginLeft: 6 }}>
          {s.admissionNumber}
        </span>
      </td>
      {s.subjects.map((m) => (
        <td key={m.subject} className="num"
          style={{ color: m.isAbsent ? 'var(--danger)' : m.marks !== null && m.maxMarks > 0
            && m.marks / m.maxMarks < 0.4 ? 'var(--danger)' : undefined }}>
          {m.isAbsent ? 'ab' : m.marks === null ? '' : m.marks}
        </td>
      ))}
      <td className="num">{s.max > 0 ? `${s.obtained}/${s.max}` : '—'}</td>
      <td className="num" style={{ fontWeight: 650 }}>{s.max > 0 ? `${s.percent.toFixed(1)}%` : '—'}</td>
    </tr>
  );
}
